import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { setGrab, setGrabbing } from "./dragCursor";

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  opacity: 0.85;
  pointer-events: none;

  @media (max-width: 900px) {
    opacity: 0.5;
  }
`;

/* ヒーロー背景のパーティクル波形グリッド（スクロールでヨー回転）
   scale: グリッド全体の大きさの倍率
   speed: 波のアニメーション速度の倍率 */
const WaveCanvas = ({ scale = 1, speed = 1 }) => {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    const cx = cv.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    /* styled側のSP用opacityはJSの毎フレーム上書きで消えるため、基準値もJSで持つ */
    const mqSP = window.matchMedia("(max-width: 900px)");
    const hero = cv.parentElement;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let vw = 0;
    let vh = 0;
    let raf = 0;
    let alive = true;

    const size = () => {
      vw = hero.clientWidth;
      vh = hero.clientHeight;
      cv.width = vw * DPR;
      cv.height = vh * DPR;
    };
    size();

    const N = 32;
    const GAP = 26;
    let t = 0;
    let rot = 0;
    const pts = [];

    /* ---------- ドラッグ回転（マウス限定・OrbCanvasと同じ操作感） ---------- */
    let yawOff = 0;
    let yawVel = 0;
    let dragging = false;
    let lx = 0;
    let cursorSet = false;
    const INTERACTIVE =
      "a,button,input,textarea,select,label,p,h1,h2,h3,h4,ul,ol,table,img,span,em,strong";
    /* h5/dt/dd/div直下のテキストなどセレクタで拾えない文字の選択まで奪わないよう、
       要素が直接テキストを持つ場合もドラッグ対象外にする */
    const hasOwnText = (el) =>
      !!el.childNodes &&
      Array.from(el.childNodes).some(
        (n) => n.nodeType === 3 && n.textContent.trim(),
      );
    const canGrab = (e) => {
      const r = cv.getBoundingClientRect();
      return (
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom &&
        !(e.target.closest && e.target.closest(INTERACTIVE)) &&
        !hasOwnText(e.target)
      );
    };
    const onDown = (e) => {
      if (e.button !== 0 || e.pointerType !== "mouse" || !canGrab(e)) return;
      dragging = true;
      lx = e.clientX;
      yawVel = 0;
      setGrabbing(true);
      document.body.style.userSelect = "none";
    };
    const onMove = (e) => {
      if (dragging) {
        const dx = e.clientX - lx;
        lx = e.clientX;
        /* 掴んだ面がドラッグ方向に付いてくる向きに回す */
        yawOff -= dx * 0.004;
        yawVel = -dx * 0.004;
        if (reduce) schedule();
      } else if (e.pointerType === "mouse") {
        const grab = canGrab(e);
        if (grab !== cursorSet) {
          setGrab(grab);
          cursorSet = grab;
        }
      }
    };
    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      if (reduce) yawVel = 0; /* reduced-motion時は慣性で回さない */
      setGrabbing(false);
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);

    const draw = () => {
      if (!alive) return;
      cx.setTransform(DPR, 0, 0, DPR, 0, 0);
      cx.clearRect(0, 0, vw, vh);
      /* グリッドの中心を右寄りに置き、画面幅に応じて拡大縮小 */
      const cxr = vw * 0.68;
      const cyr = vh * 0.52;
      const SC = Math.min(Math.max(vw / 950, 0.45), 1.7) * scale;

      const scrollY = window.scrollY;
      if (!dragging && !reduce) {
        yawOff += yawVel; /* 離した後は慣性で回り続けて減衰 */
        yawVel *= 0.95;
      }
      const rotTarget = scrollY * 0.009 + yawOff;
      /* reduced-motion時は単発描画なので補間せず目標値へ直接スナップ(OrbCanvasと同方式) */
      if (reduce) rot = rotTarget;
      else rot += (rotTarget - rot) * 0.09;
      const cosR = Math.cos(rot);
      const sinR = Math.sin(rot);

      /* parallax: グリッドがスクロールにゆっくり追従 + 横揺れ */
      const sway = Math.sin(scrollY * 0.0022) * 46;
      cv.style.transform = `translateY(${(scrollY * 0.45).toFixed(
        1,
      )}px) translateX(${sway.toFixed(1)}px)`;
      cv.style.opacity = Math.max(0, (mqSP.matches ? 0.5 : 0.85) - scrollY / 1400);

      for (let i = 0; i < N; i++) {
        for (let j = 0; j < N; j++) {
          const gx = (i - N / 2) * GAP;
          const gy = (j - N / 2) * GAP;
          const x = gx * cosR - gy * sinR;
          const y = gx * sinR + gy * cosR;
          const d = Math.sqrt(gx * gx + gy * gy);
          const z = Math.sin(d * 0.024 - t) * 22;
          pts[i * N + j] = {
            x: cxr + (x - y) * 0.86 * SC,
            y: cyr + ((x + y) * 0.5 - z) * SC,
            d,
            z,
            h: (d * 0.6 + t * 26) % 360,
          };
        }
      }

      /* 左側(テキスト領域)ほど薄くするフェード */
      const fadeL = (px) =>
        Math.min(1, Math.max(0.1, (px / vw - 0.3) / 0.32));
      cx.lineWidth = 1;
      for (let i = 0; i < N; i++) {
        for (let j = 0; j < N; j++) {
          const p = pts[i * N + j];
          const a = Math.max(0, 0.24 - p.d / 1400) * fadeL(p.x);
          cx.strokeStyle = `hsla(${p.h},70%,72%,${a})`;
          if (j < N - 1) {
            const q = pts[i * N + j + 1];
            cx.beginPath();
            cx.moveTo(p.x, p.y);
            cx.lineTo(q.x, q.y);
            cx.stroke();
          }
          if (i < N - 1) {
            const q = pts[(i + 1) * N + j];
            cx.beginPath();
            cx.moveTo(p.x, p.y);
            cx.lineTo(q.x, q.y);
            cx.stroke();
          }
        }
      }

      for (let i = 0; i < N; i++) {
        for (let j = 0; j < N; j++) {
          const p = pts[i * N + j];
          const a = Math.max(0.05, 0.75 - p.d / 530) * fadeL(p.x);
          const r = Math.max(0.7, (2.3 - p.d / 360 + p.z * 0.03) * SC);
          cx.beginPath();
          cx.arc(p.x, p.y, r, 0, Math.PI * 2);
          cx.fillStyle = `hsla(${p.h},75%,${p.z > 6 ? 78 : 68}%,${a})`;
          cx.fill();
        }
      }

      t += 0.028 * speed;
      if (!reduce) raf = requestAnimationFrame(draw);
    };

    /* reduced-motion時の単発描画。同一フレームに複数イベントが来ても1回に集約 */
    let pending = false;
    const schedule = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        draw();
      });
    };
    /* リサイズはcanvasのwidth/height変更で内容が消えるため、必ず再描画とセットで */
    const onResize = () => {
      size();
      if (reduce) schedule();
    };
    window.addEventListener("resize", onResize, { passive: true });

    draw();
    /* reduced-motion: スクロール時のみ再描画 */
    const onScroll = () => schedule();
    if (reduce) window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (dragging) setGrabbing(false);
      if (cursorSet) setGrab(false);
      document.body.style.userSelect = "";
      if (reduce) window.removeEventListener("scroll", onScroll);
    };
  }, [scale, speed]);

  return <Canvas ref={ref} aria-hidden="true" />;
};

export default WaveCanvas;
